'use client'

import React, { createContext, useCallback, useContext, useState, useEffect } from 'react'
import { useParams, usePathname } from 'next/navigation'
import canUseDOM from '@/utilities/canUseDOM'

export type Language = 'en' | 'fr' | 'ar'

export interface ContextType {
  language: Language
  setLanguage: (language: Language) => void
}

// Default to English
const initialContext: ContextType = {
  language: 'en',
  setLanguage: () => null,
}

const LanguageContext = createContext<ContextType>(initialContext)

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const params = useParams()
  const pathname = usePathname()

  // Initialize with the language from URL params if available
  const [language, setLanguageState] = useState<Language>((params?.lang as Language) || 'en')

  // Update language when URL params change
  useEffect(() => {
    if (params?.lang && (params.lang === 'en' || params.lang === 'fr' || params.lang === 'ar')) {
      setLanguageState(params.lang as Language)
    }
  }, [params?.lang])

  // Alternative detection: also check pathname directly
  useEffect(() => {
    if (pathname) {
      const match = pathname.match(/^\/(en|fr|ar)\//)
      if (match && match[1] && (match[1] === 'en' || match[1] === 'fr' || match[1] === 'ar')) {
        setLanguageState(match[1] as Language)

        // Also update the HTML lang attribute
        if (canUseDOM) {
          document.documentElement.setAttribute('lang', match[1])
        }
      }
    }
  }, [pathname])

  // Function to change language and navigate
  const setLanguage = useCallback((languageToSet: Language) => {
    // Update state
    setLanguageState(languageToSet)

    // Update HTML lang attribute
    if (canUseDOM) {
      document.documentElement.setAttribute('lang', languageToSet)

      // Navigate to new URL with language prefix
      const currentPath = window.location.pathname
      const cleanPath = currentPath.replace(/^\/(en|fr|ar)(?:\/|$)/, '/')
      window.location.href = `/${languageToSet}${cleanPath}`
    }
  }, [])

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): ContextType => useContext(LanguageContext)

